import { RECOMMENDED, REQUIRED, t, te } from "../types.ts";
import type {
  ChecklistItem,
  ChecklistSectionConfig,
  TranslatedText,
} from "../types.ts";

function item(
  requirement: TranslatedText,
  required: TranslatedText,
  details: TranslatedText,
  description: TranslatedText = te(""),
): ChecklistItem {
  return { requirement, required, details, description };
}

export const automation: ChecklistSectionConfig = {
  id: "automation",
  gradient: "from-indigo-600 to-indigo-700",
  hoverBg: "indigo-500",
  title: t("Automation", "Automatizācija", "Automazione"),
  tagline: t(
    "Connecting the digital tools to remove manual repetition",
    "Digitālo rīku savienošana, lai novērstu manuālu atkārtošanos",
    "Collegare gli strumenti digitali per rimuovere la ripetizione manuale",
  ),
  subsections: [
    {
      title: t(
        "3.1. Automation Design",
        "3.1. Automatizācijas Projektēšana",
        "3.1. Progettazione dell'Automazione",
      ),
      items: [
        item(
          t(
            "Automation Suitability",
            "Automatizācijas Piemērotība",
            "Idoneità all'Automazione",
          ),
          REQUIRED,
          t(
            "Stability ≥ 3–6 months? Volume threshold met? Exception rate low enough?",
            "Stabilitāte ≥ 3–6 mēneši? Apjoma slieksnis sasniegts? Izņēmumu īpatsvars pietiekami zems?",
            "Stabilità ≥ 3–6 mesi? Soglia di volume raggiunta? Tasso di eccezioni sufficientemente basso?",
          ),
          t(
            "If exceptions > ~20%, automation may add more risk than it saves. Measure before building.",
            "Ja izņēmumi > ~20%, automatizācija var radīt lielāku risku nekā ietaupīt. Mēriet pirms veidošanas.",
            "Se le eccezioni > ~20%, l'automazione potrebbe aggiungere più rischio di quanto ne risparmia. Misurare prima di costruire.",
          ),
        ),
        item(
          t("Input Trigger", "Sākuma Notikums", "Trigger di Input"),
          REQUIRED,
          t(
            "What event starts the automation?",
            "Kāds notikums sāk automatizāciju?",
            "Quale evento avvia l'automazione?",
          ),
          t(
            "e.g., 'New Lead in CRM', 'Date Arrived', 'Status Changed to Approved'.",
            "piem., 'Jauns kontakts CRM', 'Datums pienācis', 'Statuss mainīts uz Apstiprināts'.",
            "es., 'Nuovo Lead nel CRM', 'Data Arrivata', 'Stato Cambiato in Approvato'.",
          ),
        ),
        item(
          t("Output Outcome", "Izvades Rezultāts", "Risultato di Output"),
          REQUIRED,
          t(
            "What is the desired end result of the automation?",
            "Kāds ir automatizācijas vēlamais gala rezultāts?",
            "Qual è il risultato finale desiderato dell'automazione?",
          ),
          t(
            "e.g., 'Send welcome email', 'Create invoice in accounting system', 'Update status to Processed'.",
            "piem., 'Nosūtīt sveiciena e-pastu', 'Izveidot rēķinu grāmatvedības sistēmā', 'Atjaunināt statusu uz Apstrādāts'.",
            "es., 'Inviare email di benvenuto', 'Creare fattura nel sistema contabile', 'Aggiornare lo stato a Elaborato'.",
          ),
        ),
        item(
          t("Tool Type", "Rīka Tips", "Tipo di Strumento"),
          REQUIRED,
          t(
            "No-Code, Low-Code, or Pro-Code — which fits the complexity and team skills?",
            "No-Code, Low-Code vai Pro-Code — kas atbilst sarežģītībai un komandas prasmēm?",
            "No-Code, Low-Code o Pro-Code — quale si adatta alla complessità e alle competenze del team?",
          ),
          t(
            "No-Code: Make, Zapier, n8n; Low-Code: Google Apps Script, Power Automate; Pro-Code: Python, AWS Lambda.",
            "No-Code: Make, Zapier, n8n; Low-Code: Google Apps Script, Power Automate; Pro-Code: Python, AWS Lambda.",
            "No-Code: Make, Zapier, n8n; Low-Code: Google Apps Script, Power Automate; Pro-Code: Python, AWS Lambda.",
          ),
        ),
        item(
          t("Human Interaction", "Cilvēka Iesaiste", "Interazione Umana"),
          REQUIRED,
          t(
            "Does the flow need a human review step or a UI interface?",
            "Vai plūsmai nepieciešams cilvēka pārskatīšanas solis vai UI saskarne?",
            "Il flusso necessita di un passaggio di revisione umana o di un'interfaccia UI?",
          ),
          t(
            "Add mandatory review gates for sensitive outputs (e.g., financial transactions, contract generation).",
            "Pievienojiet obligātus pārskatīšanas kontrolpunktus sensitīviem izvades rezultātiem (piem., finanšu darījumi, līgumu ģenerēšana).",
            "Aggiungere gate di revisione obbligatori per output sensibili (es., transazioni finanziarie, generazione di contratti).",
          ),
        ),
      ],
    },
    {
      title: t(
        "3.2. Automated System Resilience",
        "3.2. Automatizētās Sistēmas Noturība",
        "3.2. Resilienza del Sistema Automatizzato",
      ),
      items: [
        item(
          t(
            "Failure Management & Manual Fallbacks",
            "Kļūmju Pārvaldība un Manuālie Rezerves Varianti",
            "Gestione dei Guasti e Fallback Manuali",
          ),
          REQUIRED,
          t(
            "How is failure detected and notified? Is there a documented manual fallback?",
            "Kā tiek atklātas un paziņotas kļūmes? Vai ir dokumentēts manuālais rezerves variants?",
            "Come viene rilevato e notificato il guasto? Esiste un fallback manuale documentato?",
          ),
          t(
            "e.g., Slack alert on error + a runbook for processing the task manually while the automation is repaired.",
            "piem., Slack brīdinājums par kļūdu + darbības instrukcija uzdevuma manuālai apstrādei, kamēr automatizācija tiek labota.",
            "es., avviso Slack in caso di errore + un runbook per elaborare il task manualmente mentre l'automazione viene riparata.",
          ),
        ),
      ],
    },
  ],
};
